import { useEffect, useMemo, useRef, useState } from "react";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { StyledStaticTimePicker } from "../../styles/StyledPicker";
import Calendar from "react-calendar";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

import api from "../../api/axios";
import { AppModalCloseIcon } from "../common/AppModalShell";
import EventCreateFloatingFooter from "./EventCreateFloatingFooter";
import {
  PRESS_HOVER,
  PRESS_TAP,
  PRESS_TRANSITION,
  SUBTLE_PRESS_TAP,
} from "../../utils/motionInteractions";

dayjs.extend(utc);
dayjs.extend(timezone);

const REQUIRED_MARK_COLOR = "#ff5656";
const FIELD_BACKGROUND_CLASS = "bg-black/10";
const FIELD_BORDER_CLASS = "border-white/25";
const FIELD_PLACEHOLDER_CLASS = "placeholder:text-white/50";
const DESCRIPTION_MAX_LENGTH = 1040;
const AI_TYPING_STEP_CHARS = 5;
const AI_TYPING_STEP_MS = 18;
const GENERATE_BUTTON_BACKGROUND =
  "linear-gradient(106.85deg, rgb(235,117,152) 0%, rgb(192,42,233) 26.953%, rgb(110,67,226) 59.987%, rgb(87,148,237) 99.935%)";

function RequiredMark({ className = "" }) {
  return (
    <span
      className={`ml-1 align-top text-[24px] ${className}`}
      style={{ color: REQUIRED_MARK_COLOR }}
    >
      *
    </span>
  );
}

function emitGlobalError(message, fallbackMessage) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("global-error", {
      detail: {
        message: String(message || "").trim() || fallbackMessage,
      },
    })
  );
}

function buildScheduledForIso(formData) {
  const dateCandidate = dayjs(formData?.date);
  if (!dateCandidate.isValid()) {
    return "";
  }

  const timeCandidate = dayjs(formData?.time);
  const normalized = timeCandidate.isValid()
    ? dateCandidate
        .hour(timeCandidate.hour())
        .minute(timeCandidate.minute())
        .second(0)
        .millisecond(0)
    : dateCandidate.second(0).millisecond(0);

  return normalized.toISOString();
}

function GenerateButton({
  label,
  onClick,
  disabled = false,
  loading = false,
  positionClassName = "absolute right-[10px] top-[10px]",
  sizeClassName = "h-[25px] w-[100px] text-[11px]",
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      whileHover={PRESS_HOVER}
      whileTap={SUBTLE_PRESS_TAP}
      transition={PRESS_TRANSITION}
      className={`${positionClassName} ${sizeClassName} flex items-center justify-center gap-[5px] rounded-[100px] border border-white/20 font-semibold leading-4 text-white transition ${
        disabled || loading ? "cursor-not-allowed opacity-55" : ""
      }`}
      style={{
        backgroundImage: GENERATE_BUTTON_BACKGROUND,
      }}
    >
      <span className="whitespace-nowrap">{label}</span>
      <img
        src="/assets/events/ai_stars.svg"
        alt=""
        aria-hidden="true"
        className={`h-[11px] w-[11px] ${loading ? "animate-pulse" : ""}`}
      />
    </motion.button>
  );
}

const normalizePickerTime = (value) => {
  const candidate = dayjs(value);
  return candidate.isValid() ? candidate : dayjs();
};

const getMeridiem = (value) => (value.hour() >= 12 ? "pm" : "am");

const applyMeridiem = (value, nextMeridiem) => {
  const normalized = normalizePickerTime(value);
  const hour = normalized.hour();
  const isPm = hour >= 12;

  if (nextMeridiem === "am" && isPm) {
    return normalized.hour(hour - 12);
  }
  if (nextMeridiem === "pm" && !isPm) {
    return normalized.hour(hour + 12);
  }
  return normalized;
};

const getTimeWarningType = (value) => {
  const candidate = normalizePickerTime(value);
  const minutes = candidate.minute();
  const hour = candidate.hour();
  const minuteNotMultipleOfFive = minutes % 5 !== 0;
  const nightOrEarlyMorning = hour >= 0 && hour < 7;
  if (nightOrEarlyMorning) return "night";
  if (minuteNotMultipleOfFive) return "minute_not_multiple_of_five";
  return null;
};

const EventCreateStep1 = ({
  formData,
  handleChange,
  handleNext,
  handleBack,
  onClose = null,
  mode = "create",
  creationType,
  eventIsValidated = true,
  showBackButton = false,
  progressWidth = "33.33333333333333%",
  progressInitialWidth = null,
  quickConceptMode = false,
  morphingFromQuickConcept = false,
  quickConceptExtraFields = null,
  quickConceptFooter = null,
}) => {
  const { t, i18n } = useTranslation("events");
  const isRussianLanguage = String(
    i18n.resolvedLanguage || i18n.language || ""
  ).toLowerCase().startsWith("ru");

  const [sportTypes, setSportTypes] = useState([]);
  const [, setSportTypesLoading] = useState(false);
  const [activeTimeView, setActiveTimeView] = useState("hours");
  const [showTimeConfirmModal, setShowTimeConfirmModal] = useState(false);
  const [timeWarningType, setTimeWarningType] = useState(null);
  const [aiFollowUpText, setAiFollowUpText] = useState("");
  const [aiFollowUpAnswer, setAiFollowUpAnswer] = useState("");
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiLoadingAction, setAiLoadingAction] = useState("");
  const [aiCooldownUntil, setAiCooldownUntil] = useState(0);
  const [nowTs, setNowTs] = useState(Date.now());
  const [canUndoAiChange, setCanUndoAiChange] = useState(false);
  const [isAiTyping, setIsAiTyping] = useState(false);
  const previousDescriptionRef = useRef("");
  const aiTypingTimerRef = useRef(null);
  const descriptionTextareaRef = useRef(null);

  const lockedFields = mode === "edit" && eventIsValidated ? ["eventName", "category"] : [];
  const isLocked = (field) => lockedFields.includes(field);
  const minSelectableDate = mode === "create" ? new Date() : undefined;
  const isConcept = creationType === "concept";
  const selectedTime = normalizePickerTime(formData.time);
  const selectedMeridiem = getMeridiem(selectedTime);
  const selectedHour24 = selectedTime.format("HH");
  const selectedMinute = selectedTime.format("mm");
  const currentDescription = String(formData?.description || "");
  const hasAiBaseContext = Boolean(
    String(formData?.eventName || "").trim() &&
    (quickConceptMode || String(formData?.category || "").trim())
  );
  const isAiEditMode = Boolean(currentDescription.trim());
  const aiCooldownSeconds = Math.max(0, Math.ceil((aiCooldownUntil - nowTs) / 1000));
  const isAiCoolingDown = aiCooldownSeconds > 0;

  const capitalizeWords = (value) =>
    String(value || "").replace(/(^|\s)(\S)/g, (match, prefix, ch) => `${prefix}${ch.toLocaleUpperCase()}`);

  // Load sport types from backend when category is sport
  useEffect(() => {
    if (quickConceptMode || formData.category !== "sport") return;

    let cancelled = false;
    const loadSportTypes = async () => {
      try {
        setSportTypesLoading(true);
        const res = await api.get("/events/sport-types/");
        if (!cancelled) {
          const results = Array.isArray(res.data?.results) ? res.data.results : [];
          setSportTypes(results);
        }
      } catch (err) {
        console.error("Failed to load sport types", err);
      } finally {
        if (!cancelled) {
          setSportTypesLoading(false);
        }
      }
    };

    loadSportTypes();

    return () => {
      cancelled = true;
    };
  }, [formData.category, quickConceptMode]);

  useEffect(() => {
    return () => {
      if (aiTypingTimerRef.current) {
        window.clearTimeout(aiTypingTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isAiCoolingDown) return undefined;
    const timerId = window.setInterval(() => {
      setNowTs(Date.now());
    }, 1000);
    return () => window.clearInterval(timerId);
  }, [isAiCoolingDown]);

  const aiPayload = useMemo(
    () => ({
      event_name: String(formData?.eventName || "").trim(),
      category: quickConceptMode ? "" : String(formData?.category || "").trim(),
      sport_type: quickConceptMode ? "" : String(formData?.sportType || "").trim(),
      quick_concept: quickConceptMode,
      scheduled_for: isConcept ? "" : buildScheduledForIso(formData),
      event_format: formData?.eventFormat,
      host_format: formData?.hostFormat,
      address: String(formData?.address || "").trim(),
      link: String(formData?.link || "").trim(),
      current_description: currentDescription,
      event_language: String(formData?.eventLanguage || "").trim(),
      ui_language: String(i18n.resolvedLanguage || i18n.language || "en"),
    }),
    [
      currentDescription,
      formData,
      i18n.language,
      i18n.resolvedLanguage,
      isConcept,
      quickConceptMode,
    ]
  );

  const requestDescriptionAi = async (action, extraPayload = {}) => {
    try {
      setAiLoadingAction(action);
      const { data } = await api.post("/events/assist-description/", {
        ...aiPayload,
        action,
        ...extraPayload,
      });
      return data;
    } catch (error) {
      const retryAfter = Number(error?.response?.data?.retry_after_seconds || 0);
      if (retryAfter > 0) {
        setAiCooldownUntil(Date.now() + retryAfter * 1000);
        setNowTs(Date.now());
      }
      emitGlobalError(
        error?.response?.data?.error,
        t("event_create.step2.ai.errors.unavailable")
      );
      return null;
    } finally {
      setAiLoadingAction("");
    }
  };

  const playAiTextIntoDescription = (text, previousDescription) => {
    const nextText = String(text || "").trim().slice(0, DESCRIPTION_MAX_LENGTH);
    if (aiTypingTimerRef.current) {
      window.clearTimeout(aiTypingTimerRef.current);
    }
    previousDescriptionRef.current = previousDescription;
    setCanUndoAiChange(false);
    setIsAiTyping(true);
    handleChange("description", "");

    let index = 0;
    const tick = () => {
      index = Math.min(nextText.length, index + AI_TYPING_STEP_CHARS);
      handleChange("description", nextText.slice(0, index));
      if (index < nextText.length) {
        aiTypingTimerRef.current = window.setTimeout(tick, AI_TYPING_STEP_MS);
        return;
      }
      aiTypingTimerRef.current = null;
      setIsAiTyping(false);
      setCanUndoAiChange(true);
    };

    tick();
  };

  const openDescriptionAiPanel = async () => {
    if (aiLoadingAction || isAiCoolingDown || isAiTyping) return;
    if (!hasAiBaseContext) {
      const missingContextMessage = quickConceptMode
        ? t("quick_concept.name_first", { defaultValue: "Enter the concept name first." })
        : t("event_create.step2.ai.errors.missing_context", {
            defaultValue: "Enter the event name and category first.",
          });
      emitGlobalError(
        missingContextMessage,
        quickConceptMode ? "Enter the concept name first." : "Enter the event name and category first."
      );
      return;
    }

    setAiPanelOpen(true);
    setAiFollowUpAnswer("");
    if (isAiEditMode) {
      setAiFollowUpText("");
      return;
    }

    const data = await requestDescriptionAi("draft_question");
    if (data?.text) {
      setAiFollowUpText(String(data.text).trim());
    }
  };

  const closeDescriptionAiPanel = () => {
    setAiPanelOpen(false);
    setAiFollowUpAnswer("");
    setAiFollowUpText("");
  };

  const submitDescriptionAiRequest = async () => {
    if (!aiFollowUpAnswer.trim() || aiLoadingAction || isAiCoolingDown || isAiTyping) return;
    const action = isAiEditMode ? "edit_generate" : "draft_generate";
    const previousDescription = currentDescription;
    setAiPanelOpen(false);
    const data = await requestDescriptionAi(action, {
      organizer_response: aiFollowUpAnswer.trim(),
    });
    if (data?.text) {
      setAiFollowUpAnswer("");
      setAiFollowUpText("");
      playAiTextIntoDescription(String(data.text).trim(), previousDescription);
    } else {
      setAiPanelOpen(true);
    }
  };

  const undoDescriptionAiChange = () => {
    if (aiTypingTimerRef.current) {
      window.clearTimeout(aiTypingTimerRef.current);
      aiTypingTimerRef.current = null;
    }
    setIsAiTyping(false);
    handleChange("description", previousDescriptionRef.current || "");
    setCanUndoAiChange(false);
  };

  useEffect(() => {
    const textarea = descriptionTextareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    const maxHeight = 240;
    const nextHeight = Math.min(Math.max(textarea.scrollHeight, 120), maxHeight);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [currentDescription, mode]);

  const proceedToNextStep = () => {
    // Validate name length and presence
    if (!formData.eventName || formData.eventName.trim() === '') {
      window.dispatchEvent(new CustomEvent('global-error', {
        detail: { message: t('event_create.step1.errors.name_required') }
      }));
      return;
    }
    // Validate category selection
    if (!formData.category) {
      window.dispatchEvent(new CustomEvent('global-error', {
        detail: { message: t('event_create.step1.errors.category_required') }
      }));
      return;
    }
    // If sport selected, validate sport type
    if (formData.category === 'sport' && !formData.sportType) {
      window.dispatchEvent(new CustomEvent('global-error', {
        detail: { message: t('event_create.step1.errors.sport_type_required') }
      }));
      return;
    }
    // Validate date and time presence
    if (!isConcept && (!formData.date || !formData.time)) {
      window.dispatchEvent(new CustomEvent('global-error', {
        detail: { message: t('event_create.step1.errors.datetime_required') }
      }));
      return;
    }
    if (!isConcept) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const datePart = dayjs.tz(formData.date, tz).startOf('day');
      const timePart = dayjs.tz(formData.time, tz);
      const selectedDateTime = datePart
        .hour(timePart.hour())
        .minute(timePart.minute())
        .second(0);

      if (selectedDateTime.diff(dayjs().tz(tz), 'minute') < 20) {
        window.dispatchEvent(new CustomEvent('global-error', {
          detail: { message: t('event_create.step1.errors.too_soon') }
        }));
        return;
      }

      const formatted = selectedDateTime.format('YYYY-MM-DDTHH:mmZ');
      handleChange('date', formatted);
      handleChange('time', selectedDateTime);
      handleChange('timezone', tz);
    }
    handleNext();
  };

  const proceedToMainInfoNextStep = () => {
    if (!formData.eventName || formData.eventName.trim() === "") {
      window.dispatchEvent(new CustomEvent("global-error", {
        detail: { message: t("event_create.step1.errors.name_required") },
      }));
      return;
    }
    if (!formData.category) {
      window.dispatchEvent(new CustomEvent("global-error", {
        detail: { message: t("event_create.step1.errors.category_required") },
      }));
      return;
    }
    if (formData.category === "sport" && !formData.sportType) {
      window.dispatchEvent(new CustomEvent("global-error", {
        detail: { message: t("event_create.step1.errors.sport_type_required") },
      }));
      return;
    }
    if (!formData.description || formData.description.trim() === "") {
      window.dispatchEvent(new CustomEvent("global-error", {
        detail: {
          message: t("event_create.step1.errors.description_required", {
            defaultValue: "Please add a description.",
          }),
        },
      }));
      return;
    }
    handleNext();
  };

  const handleNextStep = () => {
    const warningType = !isConcept ? getTimeWarningType(formData.time) : null;
    if (warningType) {
      setTimeWarningType(warningType);
      setShowTimeConfirmModal(true);
      return;
    }
    proceedToNextStep();
  };

  if (mode === "create" || mode === "edit") {
    const descriptionValue = String(formData.description || "");
    const descriptionLength = descriptionValue.length;
    const generateLabel = t("event_create.step1.generate", { defaultValue: "Generate" });
    const descriptionAiBusy = Boolean(aiLoadingAction) || isAiTyping;
    const descriptionAiButtonLabel = aiLoadingAction
      ? t("event_create.step2.ai.loading_question")
      : generateLabel;
    const descriptionAiPanelTitle = isAiEditMode
      ? t("event_create.step2.ai.edit_title")
      : t("event_create.step2.ai.question_title");
    const descriptionAiAnswerPlaceholder = isAiEditMode
      ? t("event_create.step2.ai.answer_placeholder_edit")
      : t("event_create.step2.ai.answer_placeholder_draft");

    return (
      <div
        className={`relative w-full text-white ${quickConceptMode ? "h-full min-h-full overflow-x-clip" : "min-h-[100dvh] overflow-x-hidden lg:min-h-full"}`}
      >
        <div className={`relative mx-auto flex w-full max-w-[402px] flex-col px-[26px] pt-0 lg:max-w-[520px] ${quickConceptMode ? "min-h-full pb-5" : "min-h-[100dvh] pb-[calc(24px+var(--vg-safe-area-bottom))] lg:min-h-full lg:pb-[24px]"}`}>
          {(!quickConceptMode || morphingFromQuickConcept) && (
            <div className={morphingFromQuickConcept
              ? `transition-[opacity,transform] duration-[380ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${quickConceptMode ? "pointer-events-none -translate-y-2 opacity-0" : "translate-y-0 opacity-100"}`
              : ""} aria-hidden={quickConceptMode}>
              <div className="absolute left-[25px] top-[calc(20px+var(--vg-safe-area-top))] h-9 w-9 rounded-full bg-white/10" />
              <motion.button
                type="button"
                onClick={handleBack}
                tabIndex={quickConceptMode ? -1 : undefined}
                whileHover={PRESS_HOVER}
                whileTap={SUBTLE_PRESS_TAP}
                transition={PRESS_TRANSITION}
                aria-label={t("event_create.step1.back_aria", { defaultValue: "Go back" })}
                className="absolute left-[30px] top-[calc(26px+var(--vg-safe-area-top))] flex h-6 w-6 items-center justify-center text-white"
              >
                <svg aria-hidden="true" viewBox="0 0 24 24" className="h-6 w-6" fill="none">
                  <path d="M15 6L9 12L15 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </motion.button>
            </div>
          )}

          {onClose ? (
            <motion.button
              type="button"
              onClick={onClose}
              whileHover={PRESS_HOVER}
              whileTap={SUBTLE_PRESS_TAP}
              transition={PRESS_TRANSITION}
              aria-label={t("event_create.close_aria", { defaultValue: "Close" })}
              className={`absolute right-[21px] z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white ${quickConceptMode ? "top-[16px]" : "top-[calc(16px+var(--vg-safe-area-top))]"}`}
            >
              <AppModalCloseIcon />
            </motion.button>
          ) : (
            <motion.button
              type="button"
              disabled
              aria-label={t("event_create.step1.save_draft_aria", { defaultValue: "Saved drafts" })}
              className={`absolute right-[25px] flex h-9 w-9 cursor-default items-center justify-center rounded-full bg-white/10 text-white ${quickConceptMode ? "top-[20px]" : "top-[calc(20px+var(--vg-safe-area-top))]"}`}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none">
                <path d="M7 3.75h8.8L20.25 8.2v12.05H7V3.75Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M14.75 3.75V8.5h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9.75 15.25h7.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </motion.button>
          )}

          {(!quickConceptMode || morphingFromQuickConcept) && (
            <div
              className={`absolute left-1/2 top-[calc(36px+var(--vg-safe-area-top))] h-[5px] w-[230px] -translate-x-1/2 overflow-hidden rounded-[27px] bg-white/20 ${morphingFromQuickConcept ? `transition-[opacity,transform] duration-[380ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${quickConceptMode ? "translate-y-2 opacity-0" : "translate-y-0 opacity-100"}` : ""}`}
              data-event-create-progress="track"
              data-testid="event-create-progress"
            >
              <motion.div
                className="h-full rounded-[27px] bg-white"
                data-event-create-progress="fill"
                initial={{ width: progressInitialWidth || progressWidth }}
                animate={{ width: progressWidth }}
                transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          )}

          <h1 className={`text-center font-semibold tracking-[0] text-white ${morphingFromQuickConcept ? "relative min-h-9 transition-[margin-top,font-size,line-height] duration-[520ms] ease-[cubic-bezier(0.22,1,0.36,1)]" : ""} ${quickConceptMode
            ? "mt-[20px] flex min-h-9 items-center justify-center px-10 text-[24px] leading-[1.2]"
            : "mt-[calc(86px+var(--vg-safe-area-top))] text-[32px] leading-[1.12] min-[390px]:mt-[calc(96px+var(--vg-safe-area-top))] min-[420px]:mt-[calc(104px+var(--vg-safe-area-top))]"}`}>
            {morphingFromQuickConcept ? (
              <>
                <span aria-hidden={quickConceptMode ? undefined : true} className={`transition-opacity duration-[260ms] ${quickConceptMode ? "opacity-100" : "absolute inset-0 flex items-center justify-center opacity-0"}`}>
                  {t("quick_concept.author", { defaultValue: "Your concept" })}
                </span>
                <span aria-hidden={quickConceptMode ? true : undefined} className={`transition-opacity duration-[260ms] ${quickConceptMode ? "absolute inset-0 flex items-center justify-center opacity-0" : "opacity-100"}`}>
                  {t("event_create.step1.main_information", { defaultValue: "Main information" })}
                </span>
              </>
            ) : quickConceptMode
              ? t("quick_concept.author", { defaultValue: "Your concept" })
              : t("event_create.step1.main_information", { defaultValue: "Main information" })}
          </h1>

          <div className="mt-[18px] space-y-[20px]">
            <div>
              <label className="mb-[6px] block text-[24px] font-semibold leading-[1.15] text-white">
                {t(isConcept ? "event_create.step1.title_name_concept" : "event_create.step1.title_name")}
                <RequiredMark />
              </label>
              <input
                className={`h-[50px] w-full rounded-[46px] border ${FIELD_BORDER_CLASS} ${FIELD_BACKGROUND_CLASS} px-[25px] text-[15px] font-normal text-white outline-none ${FIELD_PLACEHOLDER_CLASS} focus:border-white/45 ${quickConceptMode ? "caret-white" : ""}`}
                value={formData.eventName}
                onChange={(e) => handleChange("eventName", capitalizeWords(e.target.value))}
                placeholder={t(isConcept ? "event_create.step1.placeholder_name_concept" : "event_create.step1.placeholder_name")}
                maxLength={23}
                disabled={isLocked("eventName")}
              />
            </div>

            {!quickConceptMode && <motion.div
              initial={morphingFromQuickConcept ? { opacity: 0, height: 0, y: -8 } : false}
              animate={{ opacity: 1, height: "auto", y: 0 }}
              transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <label className="mb-[6px] block text-[24px] font-semibold leading-[1.15] text-white">
                {t("event_create.step1.title_category")}
                <RequiredMark />
              </label>
              <div className="relative">
                <select
                  className={`h-[50px] w-full appearance-none rounded-[41px] border px-[25px] pr-12 text-[15px] font-normal outline-none ${
                    isLocked("category")
                      ? "cursor-not-allowed border-white/12 bg-black/5 text-white/35"
                      : `${FIELD_BORDER_CLASS} ${FIELD_BACKGROUND_CLASS} focus:border-white/45 ${formData.category ? "text-white" : "text-white/50"}`
                  }`}
                  value={formData.category}
                  onChange={(e) => {
                    handleChange("category", e.target.value);
                    if (e.target.value !== "sport") {
                      handleChange("sportType", "");
                    }
                  }}
                  required
                  disabled={isLocked("category")}
                  aria-disabled={isLocked("category")}
                >
                  <option value="" disabled className="text-black">
                    {t("event_create.step1.placeholder_category_main", { defaultValue: "Tap to choose" })}
                  </option>
                  <option value="sport" className="text-black">{t("event_create.step1.category.sport")}</option>
                  <option value="education" className="text-black">{t("event_create.step1.category.education")}</option>
                  <option value="recreation" className="text-black">{t("event_create.step1.category.recreation")}</option>
                  <option value="party" className="text-black">{t("event_create.step1.category.party")}</option>
                  <option value="traveling" className="text-black">{t("event_create.step1.category.traveling")}</option>
                  <option value="other" className="text-black">{t("event_create.step1.category.other")}</option>
                </select>
                {isLocked("category") ? (
                  <svg
                    data-testid="event-category-lock"
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    className="pointer-events-none absolute right-[19px] top-1/2 h-5 w-5 -translate-y-1/2 text-white/30"
                    fill="none"
                  >
                    <rect x="5.5" y="10" width="13" height="10" rx="3" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg
                    data-testid="event-category-chevron"
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    className="pointer-events-none absolute right-[19px] top-1/2 h-5 w-5 -translate-y-1/2 text-white/70"
                    fill="none"
                  >
                    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            </motion.div>}

            {!quickConceptMode && formData.category === "sport" && (
              <div>
                <label className="mb-[6px] block text-[20px] font-semibold leading-[1.15] text-white">
                  {t("event_create.step1.title_sport_type")}
                  <RequiredMark className="text-[20px]" />
                </label>
                <div className="relative">
                  <select
                    className={`h-[50px] w-full appearance-none rounded-[41px] border ${FIELD_BORDER_CLASS} ${FIELD_BACKGROUND_CLASS} px-[25px] pr-12 text-[15px] font-normal outline-none focus:border-white/45 ${formData.sportType ? "text-white" : "text-white/50"}`}
                    value={formData.sportType || ""}
                    onChange={(e) => handleChange("sportType", e.target.value)}
                    required
                  >
                    <option value="" disabled className="text-black">
                      {t("event_create.step1.placeholder_sport_type")}
                    </option>
                    {sportTypes.map((item) => (
                      <option key={item.value} value={item.value} className="text-black">
                        {t(`event_create.step1.sport_types.${item.value}`, {
                          defaultValue: item.label,
                        })}
                      </option>
                    ))}
                  </select>
                  <svg aria-hidden="true" viewBox="0 0 24 24" className="pointer-events-none absolute right-[19px] top-1/2 h-5 w-5 -translate-y-1/2 text-white/70" fill="none">
                    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
            )}

            <motion.div layout={morphingFromQuickConcept ? "position" : undefined}>
              <div className="mb-[6px] flex items-center justify-between gap-3">
                <label className="block text-[24px] font-semibold leading-[1.15] text-white">
                  {t("event_create.step1.description", { defaultValue: "Description" })}
                  <RequiredMark />
                </label>
                <GenerateButton
                  label={descriptionAiButtonLabel}
                  onClick={openDescriptionAiPanel}
                  disabled={isAiCoolingDown}
                  loading={descriptionAiBusy}
                  positionClassName="relative shrink-0"
                  sizeClassName={
                    isRussianLanguage
                      ? "h-[25px] w-[124px] px-[9px] text-[10px]"
                      : "h-[25px] w-[100px] text-[11px]"
                  }
                />
              </div>
              <div>
                <textarea
                  ref={descriptionTextareaRef}
                  className={`min-h-[120px] w-full resize-none rounded-2xl border ${FIELD_BORDER_CLASS} ${FIELD_BACKGROUND_CLASS} px-[25px] py-[18px] text-[15px] font-medium leading-5 text-white outline-none ${FIELD_PLACEHOLDER_CLASS} [scrollbar-width:none] focus:border-white/45 [&::-webkit-scrollbar]:hidden ${quickConceptMode ? "caret-white" : ""}`}
                  value={descriptionValue}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder={t(isConcept
                    ? "event_create.step1.description_placeholder_concept"
                    : "event_create.step1.description_placeholder", {
                    defaultValue: isConcept ? "Describe your concept" : "Describe your event",
                  })}
                  maxLength={DESCRIPTION_MAX_LENGTH}
                />
                <div
                  className="mt-1 flex min-h-[18px] flex-wrap items-center gap-x-3 gap-y-1"
                  data-testid="description-meta-row"
                >
                  <span className="mr-auto text-[11px] font-medium leading-4 text-white/50">
                    {t(isConcept
                      ? "event_create.step1.description_tip_concept"
                      : "event_create.step1.description_tip", {
                      defaultValue: isConcept
                        ? "Tip: 50+ characters usually make the concept clearer."
                        : "Tip: 50+ characters usually make the event clearer.",
                    })}
                  </span>
                  {canUndoAiChange ? (
                    <motion.button
                      type="button"
                      onClick={undoDescriptionAiChange}
                      whileHover={PRESS_HOVER}
                      whileTap={SUBTLE_PRESS_TAP}
                      transition={PRESS_TRANSITION}
                      className="h-8 rounded-[100px] border border-white/15 bg-white/6 px-4 text-[12px] font-semibold text-white/75"
                    >
                      {t("event_create.step2.ai.undo_button")}
                    </motion.button>
                  ) : null}
                  <span className="text-[11px] font-semibold leading-none text-white/50">
                    {descriptionLength}/{DESCRIPTION_MAX_LENGTH}
                  </span>
                </div>
              </div>
              {isAiCoolingDown ? (
                <div className="mt-2 text-[11px] font-medium leading-4 text-white/50">
                  {t("event_create.step2.ai.cooldown", { seconds: aiCooldownSeconds })}
                </div>
              ) : null}
              {isAiTyping ? (
                <div className="mt-2 text-[11px] font-semibold leading-4 text-[#9fbcff]">
                  {t("event_create.step2.ai.writing")}
                </div>
              ) : null}
              {aiPanelOpen ? (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                  className="mt-3 rounded-2xl border border-white/20 bg-black/15 px-4 py-4 text-white shadow-[0_12px_28px_rgba(0,0,0,0.18)]"
                >
                  <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/55">
                    <img
                      src="/assets/events/ai_stars.svg"
                      alt=""
                      aria-hidden="true"
                      className="h-[11px] w-[11px] opacity-70"
                    />
                    <span>{descriptionAiPanelTitle}</span>
                  </div>
                  {isAiEditMode ? (
                    <div className="mt-2 text-[13px] font-medium leading-5 text-white/70">
                      {t("event_create.step2.ai.edit_intro")}
                    </div>
                  ) : aiFollowUpText ? (
                    <div className="mt-2 whitespace-pre-line text-[13px] font-medium leading-5 text-white/80">
                      {aiFollowUpText}
                    </div>
                  ) : (
                    <div className="mt-2 text-[13px] font-medium leading-5 text-white/70">
                      {t("event_create.step2.ai.loading_question")}
                    </div>
                  )}
                  <textarea
                    rows={3}
                    value={aiFollowUpAnswer}
                    onChange={(event) => setAiFollowUpAnswer(event.target.value)}
                    placeholder={descriptionAiAnswerPlaceholder}
                    className="mt-3 h-[86px] w-full resize-none rounded-2xl border border-white/15 bg-black/15 px-4 py-3 text-[13px] font-medium leading-5 text-white outline-none placeholder:text-white/38 focus:border-white/35"
                  />
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <motion.button
                      type="button"
                      onClick={closeDescriptionAiPanel}
                      disabled={Boolean(aiLoadingAction)}
                      whileHover={PRESS_HOVER}
                      whileTap={SUBTLE_PRESS_TAP}
                      transition={PRESS_TRANSITION}
                      className={`h-9 rounded-[100px] border border-white/15 bg-white/6 text-[12px] font-semibold text-white/80 ${
                        aiLoadingAction ? "cursor-not-allowed opacity-45" : ""
                      }`}
                    >
                      {t("event_create.step2.ai.cancel_button")}
                    </motion.button>
                    <motion.button
                      type="button"
                      onClick={submitDescriptionAiRequest}
                      disabled={!aiFollowUpAnswer.trim() || aiLoadingAction.endsWith("_generate") || isAiCoolingDown}
                      whileHover={PRESS_HOVER}
                      whileTap={SUBTLE_PRESS_TAP}
                      transition={PRESS_TRANSITION}
                      className={`h-9 rounded-[100px] border border-white/20 text-[12px] font-semibold text-white ${
                        !aiFollowUpAnswer.trim() || aiLoadingAction.endsWith("_generate") || isAiCoolingDown
                          ? "cursor-not-allowed opacity-55"
                          : ""
                      }`}
                      style={{
                        backgroundImage: GENERATE_BUTTON_BACKGROUND,
                        boxShadow: "0 0 7px rgba(192,42,233,0.28)",
                      }}
                    >
                      {aiLoadingAction.endsWith("_generate")
                        ? t("event_create.step2.ai.loading_generate")
                        : isAiEditMode
                          ? t("event_create.step2.ai.apply_edit_button")
                          : t("event_create.step2.ai.generate_button")}
                    </motion.button>
                  </div>
                </motion.div>
              ) : null}
            </motion.div>
            {quickConceptMode ? quickConceptExtraFields : null}
          </div>

          {quickConceptMode && quickConceptFooter ? (
            <div className="mt-auto shrink-0 pt-[32px] pb-[calc(8px+var(--vg-safe-area-bottom,env(safe-area-inset-bottom,0px)))]">
              {quickConceptFooter}
            </div>
          ) : null}

          {!quickConceptMode && <div className="mt-auto flex flex-wrap items-center justify-center gap-3 pt-[24px]">
            <motion.button
              type="button"
              onClick={handleBack}
              whileHover={PRESS_HOVER}
              whileTap={PRESS_TAP}
              transition={PRESS_TRANSITION}
              className="inline-flex h-[55px] flex-none items-center justify-center whitespace-nowrap rounded-[100px] border border-white/25 bg-white/10 px-8 text-[15px] font-semibold leading-4 text-white"
            >
              {t("event_create.step1.back_plain", { defaultValue: "Go back" })}
            </motion.button>
            <motion.button
              type="button"
              onClick={proceedToMainInfoNextStep}
              whileHover={PRESS_HOVER}
              whileTap={PRESS_TAP}
              transition={PRESS_TRANSITION}
              className="inline-flex h-[55px] flex-none items-center justify-center gap-2 whitespace-nowrap rounded-[100px] border border-white/20 bg-[#3e6ffb] px-6 text-[15px] font-semibold leading-4 text-white"
            >
              <span>{t("event_create.step1.next_step", { defaultValue: "Next step" })}</span>
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none">
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </motion.button>
          </div>}
        </div>
      </div>
    );
  }

  return (
    <>
      <section className="pb-6 lg:pb-10">
        <h1 className="text-2xl font-semibold mb-1">
          {t(isConcept ? 'event_create.step1.title_name_concept' : 'event_create.step1.title_name')}
          {isLocked("eventName") && <span className="ml-1 opacity-60">🔒</span>}
        </h1>
        <input
          className={`w-full p-3 rounded-xl mb-4 ${
            isLocked("eventName") ? "bg-zinc-800 text-zinc-400" : "bg-zinc-700 text-white"
          }`}
          value={formData.eventName}
          onChange={(e) => handleChange("eventName", capitalizeWords(e.target.value))}
          placeholder={t(isConcept ? 'event_create.step1.placeholder_name_concept' : 'event_create.step1.placeholder_name')}
          maxLength={23}
          disabled={isLocked("eventName")}
        />

        <h1 className="text-2xl font-semibold mb-1">
          {t('event_create.step1.title_category')}
          {isLocked("category") && <span className="ml-1 opacity-60">🔒</span>}
        </h1>
        <select
          className={`w-full p-3 rounded-xl mb-4 ${
            isLocked("category") ? "bg-zinc-800 text-zinc-400" : "bg-zinc-700 text-white"
          }`}
          value={formData.category}
          onChange={(e) => handleChange("category", e.target.value)}
          defaultValue=""
          required
          disabled={isLocked("category")}
        >
          <option value="" disabled hidden className="text-zinc-400">
            {t(isConcept ? 'event_create.step1.placeholder_category_concept' : 'event_create.step1.placeholder_category')}
          </option>
          <option value="sport">{t('event_create.step1.category.sport')}</option>
          <option value="education">{t('event_create.step1.category.education')}</option>
          <option value="recreation">{t('event_create.step1.category.recreation')}</option>
          <option value="party">{t('event_create.step1.category.party')}</option>
          <option value="traveling">{t('event_create.step1.category.traveling')}</option>
          <option value="other">{t('event_create.step1.category.other')}</option>
        </select>

        {formData.category === 'sport' && (
          <>
            <h1 className="text-2xl font-semibold mb-1">
              {t('event_create.step1.title_sport_type')}
            </h1>
            <select
              className="w-full p-3 rounded-xl mb-4 bg-zinc-700 text-white"
              value={formData.sportType || ''}
              onChange={(e) => handleChange('sportType', e.target.value)}
              defaultValue=""
              required
            >
              <option value="" disabled hidden className="text-zinc-400">
                {t('event_create.step1.placeholder_sport_type')}
              </option>
              {sportTypes.map((item) => (
                <option key={item.value} value={item.value}>
                  {t(`event_create.step1.sport_types.${item.value}`, {
                    defaultValue: item.label,
                  })}
                </option>
              ))}
            </select>
          </>
        )}

        {!isConcept ? (
          <>
            <h1 className="text-2xl font-semibold mb-1">
              {t('event_create.step1.title_datetime')}
            </h1>

            <div className="relative mb-3">
              <div className="flex flex-col lg:flex-row lg:space-x-4">
            <div className="flex-1 bg-white text-black rounded-2xl">
              <div className="compact-calendar">
                <Calendar
                  onChange={(value) => handleChange("date", value)}
                  value={formData.date ? formData.date : null}
                  minDetail="year"
                  maxDetail="month"
                  next2Label={null}
                  prev2Label={null}
                  className="rounded-2xl w-full"
                  {...(minSelectableDate && { minDate: minSelectableDate })}
                />
              </div>
            </div>
            <div className="flex-1 flex justify-center items-center mt-2 lg:mt-0">
              <div className="flex w-full max-w-[320px] flex-col items-center">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <StyledStaticTimePicker
                    ampm
                    displayStaticWrapperAs="mobile"
                    value={selectedTime}
                    views={["hours", "minutes"]}
                    openTo="hours"
                    view={activeTimeView}
                    onChange={(value) => handleChange("time", normalizePickerTime(value))}
                    onViewChange={(nextView) => {
                      if (nextView === "hours" || nextView === "minutes") {
                        setActiveTimeView(nextView);
                      }
                    }}
                    localeText={{ timePickerToolbarTitle: "" }}
                    slotProps={{ toolbar: { hidden: true } }}
                    slots={{ actionBar: () => null }}
                  />
                </LocalizationProvider>

                <div className="mt-3 inline-flex rounded-2xl border border-white/20 bg-[#0B1320]/70 p-1 shadow-[0_10px_30px_rgba(0,0,0,0.28)]">
                  <button
                    type="button"
                    onClick={() => handleChange("time", applyMeridiem(formData.time, "am"))}
                    aria-pressed={selectedMeridiem === "am"}
                    className={`min-w-[88px] rounded-[14px] px-5 py-2 text-sm font-extrabold tracking-[0.12em] transition ${
                      selectedMeridiem === "am"
                        ? "bg-white text-[#07111D] shadow-[0_8px_18px_rgba(255,255,255,0.22)]"
                        : "text-white/72"
                    }`}
                  >
                    {t("event_create.step1.ampm_am")}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleChange("time", applyMeridiem(formData.time, "pm"))}
                    aria-pressed={selectedMeridiem === "pm"}
                    className={`min-w-[88px] rounded-[14px] px-5 py-2 text-sm font-extrabold tracking-[0.12em] transition ${
                      selectedMeridiem === "pm"
                        ? "bg-[#3B82F6] text-white shadow-[0_10px_24px_rgba(59,130,246,0.35)]"
                        : "text-white/72"
                    }`}
                  >
                    {t("event_create.step1.ampm_pm")}
                  </button>
                </div>

                <div className="mt-3 min-w-[160px] rounded-2xl border border-white/28 bg-white/[0.05] px-5 py-4 text-center shadow-[0_12px_28px_rgba(0,0,0,0.24)]">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/55">
                    {t("event_create.step1.selected_time_label")}
                  </p>
                  <div className="mt-2 flex items-center justify-center text-[30px] font-extrabold leading-none tracking-[0.04em]">
                    <button
                      type="button"
                      onClick={() => setActiveTimeView("hours")}
                      aria-pressed={activeTimeView === "hours"}
                      className={`appearance-none border-0 bg-transparent p-0 font-inherit leading-none transition-colors ${
                        activeTimeView === "hours"
                          ? "text-[#4E8FFF] [text-shadow:0_0_18px_rgba(78,143,255,0.35)]"
                          : "text-white hover:text-white/80"
                      }`}
                    >
                      {selectedHour24}
                    </button>
                    <span className="px-[2px] text-white">:</span>
                    <button
                      type="button"
                      onClick={() => setActiveTimeView("minutes")}
                      aria-pressed={activeTimeView === "minutes"}
                      className={`appearance-none border-0 bg-transparent p-0 font-inherit leading-none transition-colors ${
                        activeTimeView === "minutes"
                          ? "text-[#4E8FFF] [text-shadow:0_0_18px_rgba(78,143,255,0.35)]"
                          : "text-white hover:text-white/80"
                      }`}
                    >
                      {selectedMinute}
                    </button>
                  </div>
                </div>
              </div>
            </div>
              </div>
            </div>
          </>
        ) : null}
      </section>
      <EventCreateFloatingFooter>
      <footer
        className="w-full px-4 pb-[calc(24px+var(--vg-safe-area-bottom))] lg:px-0 lg:pb-6"
      >
        <div className="flex justify-center w-full lg:w-[500px] mx-auto gap-3">
          {showBackButton ? (
            <button
              type="button"
              onClick={handleBack}
              className="w-1/2 rounded-2xl border border-[#5f86da]/70 bg-[linear-gradient(180deg,#365fae_0%,#2a4b8f_100%)] py-3 text-lg font-semibold text-white shadow-[0_10px_26px_rgba(20,56,131,0.42)]"
            >
              {t("event_create.step1.back")}
            </button>
          ) : null}
          <button
            onClick={handleNextStep}
            className={`${showBackButton ? "w-1/2" : "w-full"} rounded-2xl border border-[#6ea7ff]/75 bg-[linear-gradient(180deg,#4a89ef_0%,#3b74dd_100%)] py-3 text-lg font-semibold text-white shadow-[0_12px_28px_rgba(46,112,238,0.42)]`}
          >
            {t('event_create.step1.btn_next')}
          </button>
        </div>
      </footer>
      </EventCreateFloatingFooter>
      {showTimeConfirmModal ? (
        <div className="fixed inset-0 z-[80] pointer-events-auto">
          <div className="absolute inset-0 z-[80] bg-black/60 backdrop-blur-sm" />
          <div className="relative z-[81] flex min-h-full items-center justify-center px-4 pt-[calc(env(safe-area-inset-top,0px)+16px)] pb-6">
          <div className="w-full max-w-[520px] rounded-[24px] border border-[#4FC3FF]/35 bg-[linear-gradient(180deg,rgba(11,28,46,0.94),rgba(8,22,36,0.9))] p-5 text-white shadow-[0_18px_50px_rgba(0,0,0,0.32)] backdrop-blur-xl sm:p-6">
            <h3 className="text-[1.2rem] font-extrabold leading-snug sm:text-[1.35rem]">
              {t("event_create.step1.time_confirm.title")}
            </h3>
            <p className="mt-3 text-[0.97rem] leading-relaxed text-white/86">
              {timeWarningType === "night"
                ? t("event_create.step1.time_confirm.description_night")
                : t("event_create.step1.time_confirm.description_minutes")}
            </p>
            <div className="mt-4 rounded-2xl border border-white/28 bg-white/[0.05] px-5 py-4 text-center shadow-[0_12px_28px_rgba(0,0,0,0.24)]">
              <div className="flex justify-center">
                <p className="inline-flex rounded-full border border-white/20 bg-[#1b2a44]/80 px-3 py-1 text-[0.75rem] uppercase tracking-[0.14em] text-white/70">
                  {t("event_create.step1.selected_time_label")}
                </p>
              </div>
              <p className="mt-2 text-center text-[2rem] font-extrabold leading-none text-white">
                {normalizePickerTime(formData.time).format("HH:mm")}
              </p>
            </div>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => setShowTimeConfirmModal(false)}
                className="w-full rounded-2xl border border-white/25 bg-white/5 py-3 text-[0.98rem] font-semibold text-white transition hover:bg-white/10"
              >
                {t("event_create.step1.time_confirm.change")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowTimeConfirmModal(false);
                  proceedToNextStep();
                }}
                className="w-full rounded-2xl bg-[#3F8CFF] py-3 text-[0.98rem] font-bold text-white transition hover:brightness-110"
              >
                {t("event_create.step1.time_confirm.confirm")}
              </button>
            </div>
          </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default EventCreateStep1;
